import React, { useEffect, useState } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { HelperText } from '@pulse-web-ui/helper-text';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import {
  type PersonalDocumentsFormProps,
  type UpdateProfileRequestParams,
  useUpdateProfile,
} from '@entities/index';

import {
  AnalyticEvent,
  BoundaryErrorType,
  DateFormat,
  FlkCode,
  addTestAttribute,
  allowOnlyNumbers,
  getMismatches,
  useStores,
} from '@shared/index';
import { FullWidthPrimaryButton } from '@shared/ui';

import { getPassportNumber, getPassportSerries } from '../lib';

interface SubmitPersonalDocumentsProps {
  onNextStep: () => void;
}

export const SubmitPersonalDocuments = observer(
  ({ onNextStep }: SubmitPersonalDocumentsProps) => {
    const { t } = useTranslation();
    const { showBoundary } = useErrorBoundary();
    const {
      MainStore: {
        agentContractStore: { setPersonalDocuments, profile, updateProfile },
      },
    } = useStores();
    const {
      handleSubmit,
      formState: { errors, isDirty },
      setError,
    } = useFormContext<PersonalDocumentsFormProps>();
    const [requestParams, setRequestParams] =
      useState<UpdateProfileRequestParams>({
        isComplete: false,
        profile: {
          ...profile,
          gender: undefined,
          personalDocument: undefined,
        },
      });

    const { res, error, isFetching, refetch } = useUpdateProfile(
      {
        ...requestParams,
      },
      [requestParams]
    );

    useEffect(() => {
      if (
        !!requestParams?.profile?.personalDocument?.seriesPassport?.length &&
        !!requestParams?.profile?.personalDocument?.numberPassport?.length &&
        !!requestParams?.profile?.personalDocument?.snils?.length &&
        !!requestParams?.profile?.personalDocument?.inn?.length
      ) {
        refetch();
      }
    }, [requestParams]);

    useEffect(() => {
      if (!isFetching && res) {
        if (requestParams?.profile?.personalDocument) {
          sendAnalyticEvent(AnalyticEvent.AGENT_AGREEMENT_TOSTEP_ADRESS);
          setPersonalDocuments(requestParams?.profile?.personalDocument);
        }
        if (requestParams.profile.gender) {
          updateProfile({ gender: requestParams.profile.gender });
        }
        onNextStep && onNextStep();
      }
    }, [isFetching, res]);

    useEffect(() => {
      if (error) {
        const mismatches = getMismatches(error);

        if (!!mismatches) {
          mismatches.forEach(({ code }) => {
            if (
              [
                FlkCode.AGT_01_0012,
                FlkCode.AGT_01_0013,
                FlkCode.AGT_01_0014,
              ].includes(code)
            ) {
              setError('root.serverError', {
                type: code,
              });
            } else {
              showBoundary(BoundaryErrorType.SomethingWentWrong);
            }
          });
        } else {
          showBoundary(BoundaryErrorType.SomethingWentWrong);
        }
      }
    }, [error]);

    const submitPage = handleSubmit((values) => {
      if (!isDirty) {
        sendAnalyticEvent(AnalyticEvent.AGENT_AGREEMENT_TOSTEP_ADRESS);
        onNextStep();
      }

      const {
        gender,
        passportSeriesAndNumber,
        codeDepartment,
        nameDepartment,
        dateIssue,
        snils,
        inn,
        website,
      } = values;

      setRequestParams((prevParams) => {
        const dateIpRegistration =
          profile?.personalDocument?.dateIpRegistration;
        const ogrnIp = profile?.personalDocument?.ogrnIp;

        return {
          ...prevParams,
          profile: {
            ...prevParams.profile,
            gender,
            personalDocument: {
              seriesPassport: getPassportSerries(passportSeriesAndNumber),
              numberPassport: getPassportNumber(passportSeriesAndNumber),
              codeDepartment,
              nameDepartment,
              dateIssue: format(new Date(dateIssue), DateFormat.YYYY_MM_DD),
              snils: allowOnlyNumbers(snils),
              inn,
              website,
              // Prevent removing ogrnIp and dateIpRegistration that can be filled on AgentEmploymentDetailsForm step
              ogrnIp,
              dateIpRegistration,
            },
          },
        };
      });
    });

    return (
      <HelperText
        status='error'
        message={
          errors?.root?.serverError.type &&
          t('COMMON:errors.duplicatedData', {
            code: errors.root.serverError.type,
          })
        }
      >
        <FullWidthPrimaryButton
          size='l'
          onClick={submitPage}
          disabled={!!Object.keys(errors).length}
          isLoading={isFetching}
          {...addTestAttribute('personalDocuments.submitButton')}
        >
          {t('COMMON:buttons.continue')}
        </FullWidthPrimaryButton>
      </HelperText>
    );
  }
);
